import NitroService from "../../../utils/nitro.service";
import {
  IFavoriteOriginOptionExtension,
  IFavoriteOriginPricesResponse,
  IFavoriteOriginWithMonths,
  ModeValueRow,
  MonthHalf,
} from "../../../types/IFavoriteOrigins";
import { mapFavoritesByMarketerToLegacy } from "../../../utils/favoriteOriginsMapper";

type PriceInputStateParams = {
  origin: IFavoriteOriginWithMonths;
  modeRow: ModeValueRow;
  month: string;
  half: MonthHalf;
  dragHoverPriceKey: string | null;
  draftPrices: Record<string, string>;
  draftPriceShowAs: Record<string, string>;
  currentShowAs: string | null;
  isNewlyAddedOrigin?: boolean;
  getPriceInputKey: (
    origin: IFavoriteOriginWithMonths,
    modeKey: string,
    month: string,
    half: MonthHalf,
  ) => string;
};

export const getPriceInputState = ({
  origin,
  modeRow,
  month,
  half,
  dragHoverPriceKey,
  draftPrices,
  draftPriceShowAs,
  currentShowAs,
  isNewlyAddedOrigin = false,
  getPriceInputKey,
}: PriceInputStateParams) => {
  const priceKey = getPriceInputKey(origin, modeRow.key, month, half);
  const isDragHover = dragHoverPriceKey === priceKey;
  const previousValue = modeRow.value;
  const previousShowAs = modeRow.showAs ?? "PRICE";
  const hasExistingValue =
    previousValue !== null && previousValue !== undefined;
  const hasExistingState =
    hasExistingValue ||
    previousShowAs === "DISCUSS" ||
    previousShowAs === "NO_OFFER";
  const isEdited =
    (draftPrices[priceKey] !== undefined ||
      draftPriceShowAs[priceKey] !== undefined) &&
    (!isNewlyAddedOrigin || hasExistingState);
  const isDiscussOrNoOffer =
    currentShowAs === "DISCUSS" || currentShowAs === "NO_OFFER";
  let inputStateClass = "";
  if (isDragHover) inputStateClass += " dom-price-input--drag-hover";
  if (isEdited) inputStateClass += " dom-price-input--edited";
  if (isDiscussOrNoOffer && isEdited) {
    inputStateClass += " dom-price-input--highlight";
  }

  return {
    priceKey,
    inputStateClass,
    isReadOnly: isDiscussOrNoOffer,
  };
};

type SaveAndRefreshFavoriteOriginPricesOptions = {
  onLoadingChange?: (isLoading: boolean) => void;
  onError?: (error: Error) => void;
};

const toSaveAndRefreshError = (error: unknown): Error => {
  if (error instanceof Error) {
    return new Error(
      `Failed to save and refresh favorite origin prices: ${error.message}`,
    );
  }

  return new Error("Failed to save and refresh favorite origin prices.");
};

export const saveAndRefreshFavoriteOriginPrices = async (
  marketer: string,
  product: string,
  favorites: IFavoriteOriginWithMonths[],
  options?: SaveAndRefreshFavoriteOriginPricesOptions,
): Promise<IFavoriteOriginPricesResponse[]> => {
  options?.onLoadingChange?.(true);

  try {
    const normalized = await NitroService.getFavoritesByMarketer({ marketer });
    const favoriteSet = normalized
      .flatMap((entry) => entry.favoriteSets || [])
      .find((set) => set.product === product);

    const currentFavoriteLocations = favoriteSet?.favoriteLocations || [];
    const currentByKey = new Map<string, { id: string }>();

    currentFavoriteLocations.forEach((location) => {
      const rowKey = `${location.location.id}|${
        location.competitor ? "COMPETITOR" : "KOCH"
      }`;
      currentByKey.set(rowKey, { id: location.id });
    });

    const desiredByKey = new Map<string, IFavoriteOriginWithMonths>();
    favorites.forEach((favorite) => {
      const rowKey = `${favorite.location.id}|${
        favorite.competitor ? "COMPETITOR" : "KOCH"
      }`;
      desiredByKey.set(rowKey, favorite);
    });

    const createResults = new Map<string, string>();

    const toCreate = favorites
      .map((favorite, index) => ({ favorite, index }))
      .filter(({ favorite }) => {
        const rowKey = `${favorite.location.id}|${
          favorite.competitor ? "COMPETITOR" : "KOCH"
        }`;
        return !currentByKey.has(rowKey);
      });

    const createResponses = await Promise.all(
      toCreate.map(({ favorite, index }) =>
        NitroService.createFavorite({
          marketer,
          product,
          locationId: favorite.location.id,
          competitor: favorite.competitor,
          order: index,
        }),
      ),
    );

    toCreate.forEach(({ favorite }, i) => {
      const rowKey = `${favorite.location.id}|${
        favorite.competitor ? "COMPETITOR" : "KOCH"
      }`;
      const created = createResponses[i][0];
      if (created?.favLocId) {
        createResults.set(rowKey, created.favLocId);
      }
    });

    const toDelete = Array.from(currentByKey.entries()).filter(
      ([rowKey]) => !desiredByKey.has(rowKey),
    );

    await Promise.all(
      toDelete.map(([, existing]) =>
        NitroService.deleteFavorite({
          favSetId: favoriteSet?.id || `${marketer}|${product}`,
          favLocId: existing.id,
        }),
      ),
    );

    const orderedFavLocMaps = favorites
      .map((favorite, order) => {
        const rowKey = `${favorite.location.id}|${
          favorite.competitor ? "COMPETITOR" : "KOCH"
        }`;
        const currentId = currentByKey.get(rowKey)?.id;
        const createdId = createResults.get(rowKey);
        const id = currentId || createdId || (favorite as { id?: string }).id;
        if (!id) return null;
        return { id, order };
      })
      .filter(Boolean) as Array<{ id: string; order: number }>;

    if (orderedFavLocMaps.length > 0) {
      await NitroService.reorderFavorites({
        favSetId: favoriteSet?.id || `${marketer}|${product}`,
        orderedFavLocMaps,
      });
    }

    const refreshedNormalized = await NitroService.getFavoritesByMarketer({
      marketer,
    });
    const refreshedFavorites =
      mapFavoritesByMarketerToLegacy(refreshedNormalized);
    return refreshedFavorites;
  } catch (error) {
    const wrappedError = toSaveAndRefreshError(error);
    options?.onError?.(wrappedError);
    throw wrappedError;
  } finally {
    options?.onLoadingChange?.(false);
  }
};

const normalizeOutboundModes = (rawModes: string[]): string[] =>
  rawModes
    .map((mode) => String(mode).trim().toUpperCase())
    .filter((mode) => mode === "TRUCK" || mode === "RAIL");

export const createResolveOutboundModes = (locationId: string) => {
  let fetchedDetailModes: string[] | null = null;

  return async () => {
    if (fetchedDetailModes === null) {
      try {
        const details = await NitroService.getMultiLocationDetails({
          ids: [locationId],
        });
        const firstDetail = details?.[0];
        const detailModes = firstDetail ? firstDetail.outboundModes : [];
        fetchedDetailModes = normalizeOutboundModes(detailModes);
      } catch {
        fetchedDetailModes = [];
      }
    }

    return fetchedDetailModes;
  };
};

type BuildNewFavoriteOriginRowParams = {
  option: { id: string; name: string };
  competitor: boolean;
  sourceOption: IFavoriteOriginOptionExtension | undefined;
  selectedMonths: string[];
  currentMonth: number;
  currentYear: number;
  months: string[];
  resolveOutboundModes: (
    sourceOption: IFavoriteOriginOptionExtension | undefined,
  ) => Promise<string[]>;
};

export const buildNewFavoriteOriginRow = async ({
  option,
  competitor,
  sourceOption,
  selectedMonths,
  currentMonth,
  currentYear,
  months,
  resolveOutboundModes,
}: BuildNewFavoriteOriginRowParams): Promise<IFavoriteOriginWithMonths> => {
  const outboundModesForNewRow = await resolveOutboundModes(sourceOption);

  return {
    location: {
      id: option.id,
      name: option.name,
      outboundModes: outboundModesForNewRow,
    },
    competitor,
    months: selectedMonths.map((month) => {
      const monthIndex = months.indexOf(month);
      const monthNum = monthIndex + 1;
      const year = monthIndex >= currentMonth ? currentYear : currentYear + 1;
      const lastDay = new Date(year, monthNum, 0).getDate();
      return {
        month: { year, month: monthNum, day: 1 },
        isSplit: false,
        periods: [
          {
            startDate: {
              year,
              month: monthNum,
              day: 1,
            },
            endDate: {
              year,
              month: monthNum,
              day: lastDay,
            },
            prices: {
              posted: {
                price: null,
                showAs: "PRICE",
              },
              truck: {
                price: null,
                showAs: "PRICE",
              },
              rail: {
                price: null,
                showAs: "PRICE",
              },
            },
          },
        ],
      };
    }),
  };
};
